import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { NavLink } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import { useQuery, useMutation, useApolloClient } from '@apollo/client';

import {
  GET_INFOUSER,
  LOGIN_MUTATION,
  LOGIN_WITH_GOOGLE_MUTATION,
} from '@betwixt/common/graphql/user.js';

import { OAuth } from '@betwixt/common/utils/auth.js';
import { SetError } from '@betwixt/common';
import Header from '../components/Header.js';
import Footer from '../components/Footer.js';

const Login = (props) => {
  const { gConfig } = props;
  const client = useApolloClient();
  const goto = new URLSearchParams(props.location.search).get('goto');
  const [username, setUsername] = useState(null);
  const [password, setPassword] = useState(null);
  const [errors, setErrors] = useState({});
  const [isMounted, setIsMounted] = useState(true);
  const [googleStatus, setGoogleStatus] = useState('loading');
  const [googleError, setGoogleError] = useState(null);
  const googleButtonRef = useRef(null);
  const googleCredentialHandlerRef = useRef(null);

  const { data } = useQuery(GET_INFOUSER, {
    fetchPolicy: 'cache-first',
  });
  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [loginWithGoogleMutation] = useMutation(LOGIN_WITH_GOOGLE_MUTATION);

  const User = data?.User;

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!User || !isMounted || !User.modulesActived?.length) {
      return;
    }

    const nextPath = goto
      ? `/${goto}${props.location.search}`
      : `/${User.modulesActived[0].routes.my.path}`;
    const currentPath = `${props.location.pathname}${props.location.search}`;

    if (currentPath !== nextPath) {
      props.history.replace(nextPath);
    }
  }, [User, goto, isMounted, props.history, props.location.pathname, props.location.search]);

  const setResponseErrors = (responseErrors, setErrorState) => {
    const nextErrors = {};

    (responseErrors || []).forEach(({ path, message }) => {
      nextErrors[`${path}Error`] = message;
    });

    if (isMounted) setErrorState(nextErrors);
  };

  const completeLogin = async (loginResponse, onError) => {
    if (!loginResponse?.ok) {
      onError(loginResponse?.errors || [{ path: 'form', message: props.t('errors.loginFailed') }]);
      return false;
    }

    localStorage.setItem('token', loginResponse.token);
    localStorage.setItem('refreshToken', loginResponse.refreshToken);

    const user = loginResponse.user?.clientId
      ? await OAuth.setClientData(client, loginResponse.user.clientId)
      : null;

    if (!user) {
      onError([{ path: 'form', message: props.t('errors.sessionUnavailable') }]);
      return false;
    }

    if (isMounted) {
      props.history.push(`/${user.modulesActived[0].routes.my.path}${props.location.search}`);
    }

    return true;
  };

  const handleGoogleCredential = async (credential) => {
    if (!credential || !isMounted) return;

    setGoogleError(null);

    try {
      const { data } = await loginWithGoogleMutation({
        variables: { credential },
      });

      const completed = await completeLogin(data.loginWithGoogle, (responseErrors) => {
        const firstError = responseErrors?.[0]?.message || props.t('errors.googleLoginFailed');
        if (isMounted) setGoogleError(firstError);
      });

      if (!completed && isMounted) setGoogleStatus('ready');
    } catch (err) {
      if (isMounted) {
        setGoogleStatus('ready');
        setGoogleError(err.message || props.t('errors.googleLoginFailed'));
      }
    }
  };

  googleCredentialHandlerRef.current = handleGoogleCredential;

  useEffect(() => {
    let active = true;
    let script = null;
    const googleClientId = gConfig?.google?.myClientId;

    const initializeGoogle = () => {
      if (!active) return;

      if (!window.google?.accounts?.id || !googleClientId) {
        setGoogleStatus('unavailable');
        return;
      }

      if (!googleButtonRef.current) {
        setGoogleStatus('unavailable');
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => googleCredentialHandlerRef.current?.(response?.credential),
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: 300,
      });
      setGoogleStatus('ready');
    };

    if (typeof window === 'undefined') return undefined;

    setGoogleStatus('loading');

    if (!googleClientId) {
      setGoogleStatus('unavailable');
      return undefined;
    }

    if (window.google?.accounts?.id) {
      initializeGoogle();
    } else {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      script.onerror = () => active && setGoogleStatus('unavailable');
      document.head.appendChild(script);
    }

    return () => {
      active = false;
      if (script) {
        script.onload = null;
        script.onerror = null;
      }
    };
  }, [gConfig?.google?.myClientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (username === null || password === null) return;

    try {
      const { data } = await loginMutation({
        variables: { username, password },
      });

      await completeLogin(data.login, (responseErrors) => {
        setResponseErrors(responseErrors, setErrors);
      });
    } catch (err) {
      SetError('inMODAL', 'Network error', err.message, client, props.history);
    }
  };

  return (
    <main id="LoginPage" role="main">
      <Header pagename="login" gConfig={gConfig} />

      {errors.formError != null ? <div className="error"> {errors.formError} </div> : ''}

      <section data-pagename="login">
        <div className="container section primary mt-0">
          <div className="row">
            <div className="col-md-12 col-xs-12">
              <div id="LoginPage" className="section col-md-12 col-sm-12 col-xs-12">
                <header>
                  <h3> {props.t('section.title')} </h3>
                  <p> {props.t('section.description')} </p>
                </header>

                <div aria-label={props.t('google.ariaLabel')} aria-live="polite">
                  <div ref={googleButtonRef} />
                  {googleStatus === 'loading' ? <p>{props.t('google.loading')}</p> : ''}
                  {googleStatus === 'unavailable' ? <p>{props.t('google.unavailable')}</p> : ''}
                  {googleError != null ? <div className="error"> {googleError} </div> : ''}
                </div>

                <div role="separator" style={{ textAlign: 'center', margin: '20px 0' }}>
                  {props.t('google.separator')}
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group col-md-5">
                      <input
                        id="username"
                        name="username"
                        type="text"
                        className="form-control with-border"
                        placeholder={props.i18n.getResource(
                          props.i18n.language,
                          'global',
                          'username'
                        )}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                      {errors.emailError != null ? (
                        <div className="error"> {errors.emailError} </div>
                      ) : (
                        ''
                      )}
                    </div>

                    <div className="form-group col-md-5">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        className="form-control with-border"
                        placeholder={props.i18n.getResource(
                          props.i18n.language,
                          'global',
                          'password'
                        )}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      {errors.passwordError != null ? (
                        <div className="error"> {errors.passwordError} </div>
                      ) : (
                        ''
                      )}
                    </div>

                    <div className="form-group col-md-2">
                      <button type="submit" className="btn btn-primary mb-2 w-100">
                        GO
                      </button>
                    </div>

                    {errors.genericError != null ? (
                      <div className="col-md-12 mb-4">
                        <div className="error genericError"> {errors.genericError} </div>
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', margin: '30px' }}>
          <NavLink to="/signup">
            {' '}
            {props.i18n.getResource(props.i18n.language, 'global', 'signup_2')}{' '}
          </NavLink>
        </div>
      </section>

      <Footer i18n={props.i18n} without_logo={true} type="minimal_row" gConfig={gConfig} />
    </main>
  );
};

export default withRouter(withTranslation('login')(Login));

Login.propTypes = {
  gConfig: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getResource: PropTypes.func.isRequired,
    language: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
};
